import { useLocation } from "@tanstack/react-router"
import { useMemo } from "react"

import type { GoodsReceiptDetailsTabConfig } from "../types"

import { BasicPathType, useAppView } from "@/hooks/useAppView"

interface UseGoodsReceiptDetailsTabsProps {
  goodsReceiptId: string
  baseRoute: "incoming" | "soft-received" | "checked-in" | "stored"
}

interface UseGoodsReceiptDetailsTabsReturn {
  tabs: GoodsReceiptDetailsTabConfig[]
  activeTab: GoodsReceiptDetailsTabConfig
}

export const useGoodsReceiptDetailsTabs = ({
  goodsReceiptId,
  baseRoute,
}: UseGoodsReceiptDetailsTabsProps): UseGoodsReceiptDetailsTabsReturn => {
  const location = useLocation()
  const { getBasicPathFor, isTabletView } = useAppView()

  const tabs: GoodsReceiptDetailsTabConfig[] = useMemo(
    () => [
      {
        path: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/${baseRoute}/${goodsReceiptId}/built-pallets`,
        label: "Built Pallets",
        isHidden: baseRoute !== "checked-in" && baseRoute !== "stored",
      },
      {
        path: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/${baseRoute}/${goodsReceiptId}/purchase-orders`,
        label: "Purchase Orders",
      },
      {
        path: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/${baseRoute}/${goodsReceiptId}/comments`,
        label: "Comments",
      },
      {
        path: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/${baseRoute}/${goodsReceiptId}/uploads`,
        label: "Uploads",
        isHidden: isTabletView,
      },
    ],
    [goodsReceiptId, baseRoute, getBasicPathFor, isTabletView]
  )

  const activeTab = useMemo(() => {
    const visibleTabs = tabs.filter((tab) => !tab.isHidden)
    return visibleTabs.find((tab) => location.pathname.startsWith(tab.path)) ?? visibleTabs[0]
  }, [tabs, location.pathname])

  return { tabs, activeTab }
}
